import React, { memo, useState, useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import firebase from "../firebase/config";
import { getAuth, signOut } from "firebase/auth";
import { useSession } from "../firebase/UserProvider";
import logo from "../img/BMSLogo.jpg";
import "../styles/Sidebar.css";
import ThemeSwitcher from "../components/ThemeSwitcher";

// ─── Inline styles (all values use CSS custom properties) ───────────────────
const S = {
  navbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "var(--theme-spacing-sm) var(--theme-spacing-xl)",
    backgroundColor: "var(--theme-nav-bg)",
    color: "var(--theme-nav-text)",
    borderBottom: "1px solid var(--theme-color-mute)",
    position: "sticky",
    top: 0,
    zIndex: "var(--z-navbar)",
    gap: "var(--theme-spacing-lg)",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: "var(--theme-spacing-md)",
    textDecoration: "none",
    flexShrink: 0,
  },
  logo: {
    maxHeight: "40px",
    width: "auto",
    objectFit: "contain",
  },
  title: {
    fontFamily: "var(--theme-font-family-display)",
    fontSize: "18px",
    fontWeight: 700,
    color: "var(--theme-nav-text)",
    margin: 0,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
  titleSmall: {
    fontFamily: "var(--theme-font-family-display)",
    fontSize: "15px",
    fontWeight: 700,
    color: "var(--theme-nav-text)",
    margin: 0,
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: "var(--theme-spacing-sm)",
    flexShrink: 0,
  },
  hamburger: {
    display: "none",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "var(--theme-spacing-sm)",
    color: "var(--theme-nav-text)",
    fontSize: "22px",
    lineHeight: 1,
  },
  hamburgerVisible: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  profileBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center",
  },
  avatar: {
    height: "32px",
    width: "32px",
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid var(--theme-color-mute)",
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    backgroundColor: "var(--theme-card-bg)",
    borderRadius: "var(--theme-rounded-md)",
    border: "1px solid var(--theme-color-mute)",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    minWidth: "180px",
    zIndex: "var(--z-dropdown)",
    padding: "var(--theme-spacing-sm) 0",
  },
  dropdownItem: {
    display: "block",
    width: "100%",
    padding: "var(--theme-spacing-sm) var(--theme-spacing-xl)",
    background: "none",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: "var(--theme-font-family-body)",
    fontSize: "14px",
    color: "var(--theme-color-ink)",
  },
  profileWrapper: {
    position: "relative",
  },
};

export default memo(function Header({ onSidebarToggle, sidebarOpen }) {
  const history = useHistory();
  // eslint-disable-next-line no-unused-vars
  const location = useLocation();
  const [profileNavMenuDropdown, setProfileMenuDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { user } = useSession();

  const signOutUser = async () => {
    try {
      const auth = getAuth(firebase);
      await signOut(auth);
      setProfileMenuDropdown(false);
      history.push("/login");
    } catch (error) {
      console.error("Sign out failed:", error);
    }
  };

  const toggleProfileNavMenuDropdown = () => {
    setProfileMenuDropdown((prev) => !prev);
  };

  // Close profile dropdown on route change
  useEffect(() => {
    setProfileMenuDropdown(false);
  }, []);

  // Track viewport width for responsive hamburger visibility
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    if (!profileNavMenuDropdown) return;
    const handler = () => setProfileMenuDropdown(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [profileNavMenuDropdown]);

  return (
    <nav style={S.navbar}>
      {/* ── Brand and Hamburger Left Group ── */}
      <div className="shrink-0" style={{ display: "flex", alignItems: "center", gap: "var(--theme-spacing-md)" }}>
        {/* Hamburger — mobile only */}
        {user && isMobile && (
          <button
            type="button"
            aria-label="Toggle navigation"
            aria-expanded={sidebarOpen}
            style={{
              ...S.hamburger,
              ...S.hamburgerVisible,
            }}
            onClick={onSidebarToggle}
          >
            {sidebarOpen ? "✕" : "☰"}
          </button>
        )}

        <Link to="/" style={S.brand}>
          <img src={logo} alt="Bhaktivedanta Model School logo" style={S.logo} />
          {isMobile ? (
            <span style={S.titleSmall}>
              {process.env.REACT_APP_APP_SHORT_NAME || process.env.REACT_APP_APP_TITLE}
            </span>
          ) : (
            <h1 style={S.title}>{process.env.REACT_APP_APP_TITLE}</h1>
          )}
        </Link>
      </div>

      {/* ── Right controls ── */}
      <div style={S.right}>
        {/* Theme switcher — only when logged in */}
        {user && <ThemeSwitcher />}

        {/* Profile dropdown */}
        {user && (
          <div
            style={S.profileWrapper}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={S.profileBtn}
              type="button"
              onClick={toggleProfileNavMenuDropdown}
              aria-expanded={profileNavMenuDropdown}
              aria-label="Open profile menu"
            >
              <img
                src={user.photoURL || ""}
                alt={user.displayName || "User avatar"}
                style={S.avatar}
              />
            </button>

            {profileNavMenuDropdown && (
              <div style={S.dropdown} role="menu">
                <button
                  style={S.dropdownItem}
                  onClick={signOutUser}
                  role="menuitem"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
});
